import { z } from "zod";
import { workflowItemFailureSchema } from "./workflow-item-failure.js";
const idSchema = z.string().trim().min(1).max(256);
/** How a run started: a Test sample, the workflow's schedule, or the authoring agent's own test. */
export const workflowRunKindSchema = z.enum(["sample", "scheduled", "agent"]);
export const workflowRunDeliverySchema = z.object({
    channelId: idSchema,
    status: z.enum(["pending", "posted", "failed"]),
    /** The posted document's session, which opens it. */
    sessionId: idSchema.nullable(),
    artifactId: idSchema.nullable(),
    error: z.string().nullable(),
}).strict();
export const workflowRunSummarySchema = z.object({
    runId: idSchema,
    kind: workflowRunKindSchema,
    version: z.number().int().positive(),
    status: z.enum(["queued", "running", "succeeded", "failed", "cancelled"]),
    createdAt: z.number().int(),
    finishedAt: z.number().int().nullable(),
    error: z.string().nullable(),
    delivery: workflowRunDeliverySchema.nullable(),
}).strict();
const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;
/**
 * The run's reply as a person would write it, naming only the outcomes that
 * happened: "Processed 2 items: 1 done, 1 waiting for review or posting."
 */
export function durableWorkflowRunSummary(counts) {
    const parts = [
        counts.succeeded ? `${counts.succeeded} done` : null,
        counts.waiting ? `${counts.waiting} waiting for review or posting` : null,
        counts.rejected ? `${counts.rejected} rejected` : null,
        counts.failed ? `${counts.failed} failed` : null,
        counts.notHandled ? `${counts.notHandled} skipped` : null,
        counts.skipped ? `${counts.skipped} already finished` : null,
    ].filter((part) => part !== null);
    const processed = `Processed ${plural(counts.total, "item", "items")}`;
    return parts.length ? `${processed}: ${parts.join(", ")}.` : `${processed}.`;
}
/** A run still going, in the same words: "Running: 12 of 42 items done so far." */
export function durableWorkflowRunProgressSummary(done, total) {
    return `Running: ${done} of ${plural(total, "item", "items")} done so far.`;
}
export const listWorkflowRunsResponseSchema = z.object({
    runs: z.array(workflowRunSummarySchema),
}).strict();
/**
 * How far a durable run has taken each of its items, for anyone who can open the workflow's channel: each step's
 * status in the definition's order, the review the item raised, or why it failed. It carries no step input or
 * output, so it shows nothing the channel's members cannot already see; the full trace stays with the workflow.
 * An item `waiting_review` has raised its review and waits for a decision. A step a finished item never reached
 * is `skipped`. The run is `settled` once every item has raised its review, succeeded or failed.
 */
export const workflowRunProgressResponseSchema = z.object({
    workflowId: idSchema,
    title: z.string(),
    runId: idSchema,
    status: z.enum(["running", "settled"]),
    updatedAt: z.number().int(),
    items: z.array(z.object({
        itemId: idSchema,
        title: z.string().nullable(),
        status: z.enum(["queued", "running", "waiting_review", "succeeded", "failed", "skipped"]),
        startedAt: z.number().int().nullable(),
        steps: z.array(z.object({
            nodeId: idSchema,
            label: z.string(),
            status: z.enum(["pending", "running", "succeeded", "failed", "skipped"]),
            startedAt: z.number().int().nullable(),
            completedAt: z.number().int().nullable(),
        }).strict()).max(64),
        review: z.object({ proposalId: idSchema, batchId: idSchema.nullable(), state: z.string() }).strict().nullable(),
        failure: workflowItemFailureSchema.nullable(),
        /** For a skipped item, why the workflow did not run it, in plain words ("Skipped: a spreadsheet, …"). */
        skipReason: z.string().max(300).nullable().optional(),
    }).strict()).max(100),
}).strict();
